import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers-pro";
import { Dialog } from "components/Dialog";
import { MultipleFacilityPicker } from "components/MultipleFacilityPicker";
import { MultipleFacilityPickerFragment } from "generated-graphql/graphql";
import { DateTime } from "luxon";
import { DialogType, useMinimizedDialogs } from "providers/minimizedDialogs";
import { useEffect, useMemo, useState } from "react";

import { validateShippedDateRange } from "./exportManifestPdfsValidation";
import { ManifestPdfExportFilters, useExportManifestPdfs } from "./useExportManifestPdfs";

const PREVIEW_DEBOUNCE_MS = 400;

function normalizeWasteCodes(codes: string[]): string[] {
  const normalized = codes
    .map((code) => code.trim().toUpperCase())
    .filter((code) => code.length > 0);
  return [...new Set(normalized)];
}

type Props = { open: boolean; onClose: () => void };

export function ExportManifestPdfsDialog({ open, onClose }: Props) {
  const [facilities, setFacilities] = useState<MultipleFacilityPickerFragment[] | null>(null);
  const [start, setStart] = useState<DateTime | null>(null);
  const [end, setEnd] = useState<DateTime | null>(null);
  const [federalCodes, setFederalCodes] = useState<string[]>([]);
  const [stateCodes, setStateCodes] = useState<string[]>([]);

  const { preview, previewLoading, loadPreview, startExport, exportStatus, reset } =
    useExportManifestPdfs();
  const { createDialog } = useMinimizedDialogs();

  const dateError = validateShippedDateRange(start, end);

  const filters: ManifestPdfExportFilters = useMemo(
    () => ({
      facilityIds: facilities?.map((facility) => facility.id) ?? [],
      shippedDateStart: start?.isValid ? (start.toISODate() ?? "") : "",
      shippedDateEnd: end?.isValid ? (end.toISODate() ?? "") : "",
      federalWasteCodes: federalCodes,
      stateWasteCodes: stateCodes,
    }),
    [facilities, start, end, federalCodes, stateCodes]
  );
  const filtersKey = JSON.stringify(filters);

  useEffect(() => {
    if (!open || dateError) {
      return;
    }
    const timer = setTimeout(() => loadPreview(filters), PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(timer);
    // oxlint-disable-next-line exhaustive-deps -- keyed on filtersKey, not filters, to debounce on value changes rather than object identity
  }, [open, dateError, filtersKey]);

  const handleClose = () => {
    setFacilities(null);
    setStart(null);
    setEnd(null);
    setFederalCodes([]);
    setStateCodes([]);
    reset();
    onClose();
  };

  const isSubmitting = exportStatus === "submitting";

  // Shown in the tray dialog's title so multiple exports stay
  // distinguishable. Month-year keeps the title short enough to avoid
  // truncation in most cases; day precision rarely distinguishes exports.
  const dateRangeLabel =
    start?.isValid && end?.isValid
      ? `${start.toFormat("MMM yyyy")} – ${end.toFormat("MMM yyyy")}`
      : undefined;

  const handleExport = () => {
    startExport(filters, (jobId) => {
      // Using the jobId as the dialog id makes this idempotent against the
      // backend's identical-params dedup: re-exporting the same filters
      // returns the same jobId, so createDialog re-uses the existing tray
      // dialog instead of stacking a duplicate.
      createDialog({
        id: jobId,
        dialogType: DialogType.JobExportProgress,
        props: {
          id: jobId,
          jobId,
          title: "Manifest PDFs",
          label: dateRangeLabel,
          // The deliverables job emails 7-day signed links on completion.
          notifiesByEmail: true,
        },
      });
      handleClose();
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title="Export Manifest PDFs"
      maxWidth="sm"
      fullWidth
      primaryActionButtonProps={{
        saveText: "Export PDFs",
        onClick: handleExport,
        disabled: Boolean(dateError) || isSubmitting,
        loading: isSubmitting,
        loadingText: "Exporting…",
        hideStartIcon: true,
      }}
    >
      <Stack spacing={2.5}>
        <MultipleFacilityPicker
          facilityQueryVariant="waste"
          label="Facilities"
          value={facilities}
          onChange={setFacilities}
          textFieldProps={{
            helperText: "Optional. Select facilities to narrow the export.",
          }}
        />

        <Stack direction="row" spacing={2}>
          <DatePicker
            label="Shipped date start"
            value={start}
            onChange={setStart}
            slotProps={{
              textField: {
                fullWidth: true,
                error: Boolean(dateError),
                helperText: dateError,
              },
            }}
          />
          <DatePicker
            label="Shipped date end"
            value={end}
            onChange={setEnd}
            slotProps={{
              textField: {
                fullWidth: true,
                error: Boolean(dateError),
              },
            }}
          />
        </Stack>

        <Autocomplete
          multiple
          freeSolo
          options={[]}
          value={federalCodes}
          onChange={(_event, newValue) => setFederalCodes(normalizeWasteCodes(newValue))}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip label={option} {...getTagProps({ index })} key={option} />
            ))
          }
          renderInput={(params) => (
            <TextField {...params} label="Federal waste codes" placeholder="D001, F003…" />
          )}
        />

        <Autocomplete
          multiple
          freeSolo
          options={[]}
          value={stateCodes}
          onChange={(_event, newValue) => setStateCodes(normalizeWasteCodes(newValue))}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip label={option} {...getTagProps({ index })} key={option} />
            ))
          }
          renderInput={(params) => (
            <TextField {...params} label="State waste codes" placeholder="331, 352…" />
          )}
        />

        {!dateError && (
          <Box>
            {previewLoading && <Skeleton width={280} height={24} />}
            {!previewLoading && preview && (
              <>
                <Typography variant="body2">
                  <strong>{preview.matchingManifests.toLocaleString()}</strong> manifests match ·{" "}
                  <strong>{preview.manifestsWithPdf.toLocaleString()}</strong> have PDFs
                </Typography>
                {preview.manifestsWithPdf < preview.matchingManifests && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    Manifests without a PDF on file are listed in the export summary but no document
                    is included.
                  </Alert>
                )}
              </>
            )}
          </Box>
        )}

        <Typography variant="caption" color="text.secondary">
          You&apos;ll also receive an email with download links (valid 7 days) — safe to close this
          window after starting the export.
        </Typography>
      </Stack>
    </Dialog>
  );
}
